/*
 * Copyright (c) 2025 curryfirm.com
 */

// src/features/workflow/workflowSlice.js

import { createSlice } from '@reduxjs/toolkit';
import * as constants from '../../constants.js';

// Define the possible steps as constants or enum-like structure for clarity
export const WIZARD_STEPS = {
    CHOOSE_ROUTE_TYPE: 1,
    CONFIRM_DUMP_LOCATIONS: 2,
    CHOOSE_EMPTY_CAN: 3,
    START_LOCATION: 4,
    END_LOCATION: 5,
    DUMPSTER_SELECTION: 6,
    SUMMARY: 7,
    DISPLAY_ROUTE: 8,
};

const initialState = {
    step: WIZARD_STEPS.CHOOSE_ROUTE_TYPE,
    wizardLocationId: null,       // Location key, i.e. street|locationId, constructed from parms on URL
    storedRouteType: null,        // 'swap-and-drop' or 'to-dump-and-back'
    confirmedDumpLocationIds: [], // locationId's of dump locations not denied by user
    emptyAvailableCans: [],       // Array of can data/IDs, as confirmed at drop-lot
    startLocationChoosingMode: constants.LOCATION_CHOOSING_MODES.current,
    endLocationChoosingMode: constants.LOCATION_CHOOSING_MODES.current,
    startLocationDropLot: null,   // A location key, i.e. street|locationId
    endLocationDropLot: null,     // A location key, i.e. street|locationId
    startLocationCustomAddress: null, // A location key, i.e. street|locationId
    endLocationCustomAddress: null, // A location key, i.e. street|locationId
    chosenDumpsterIds: [],        // Array of selected dumpster IDs or objects
    routeResult: null,            // Result of routing computation, if applicable
    returnToSummaryAvailable: false,
};

const workflowSlice = createSlice({
    name: 'workflow',
    initialState,
    reducers: {
        setStep(state, action) {
            state.step = action.payload;
        },
        nextStep(state) {
            // Skip the choose-empty-can step if doing to-dump-and-back routing
            if ((state.storedRouteType === constants.ROUTE_TYPES.TO_DUMP_AND_BACK) && ((state.step + 1) === WIZARD_STEPS.CHOOSE_EMPTY_CAN)) {
                state.step = state.step + 2;
            }
            else {
                state.step += 1;
            }
        },
        prevStep(state) {
            // Skip the choose-empty-can step if doing to-dump-and-back routing
            if ((state.storedRouteType === constants.ROUTE_TYPES.TO_DUMP_AND_BACK) && ((state.step - 1) === WIZARD_STEPS.CHOOSE_EMPTY_CAN)) {
                state.step = state.step - 2;
            }
            else {
                state.step = Math.max(1, state.step - 1);
            }
        },
        setStoredRouteType(state, action) {
            if (state.storedRouteType !== action.payload) {
                state.storedRouteType = action.payload; // Immer handles copy under the hood
            }
        },
        setConfirmedDumpLocationIds(state, action) {
            state.confirmedDumpLocationIds = action.payload;
        },
        setEmptyAvailableCans(state, action) {
            state.emptyAvailableCans = action.payload;
        },
        setLocationChoosingMode(state, action) {
            // target is either 'startLocationChoosingMode' or 'endLocationChoosingMode'
            // mode is one of constants.LOCATION_CHOOSING_MODES
            const { field, value } = action.payload;
            state[field] = value;
        },
        setWizardLocationId: (state, action) => {
            state.wizardLocationId = action.payload; // A location key
        },
        setLocationDropLot(state, action) {
            const { field, value } = action.payload
            state[field] = value;
        },
        setLocationCustomAddress(state, action) {
            const { field, value } = action.payload
            state[field] = value;
        },
        setChosenDumpsterIds(state, action) {
            state.chosenDumpsterIds = action.payload;
        },
        setRouteResult(state, action) {
            state.routeResult = action.payload;
        },
        resetWorkflow(state) {
            // For reseting the whole wizard to initial
            Object.assign(state, initialState);
        },
        setReturnToSummaryAvailable(state, action) {
            state.returnToSummaryAvailable = action.payload;
        },
    }
});

export const {
    setStep,
    nextStep,
    prevStep,
    setStoredRouteType,
    setConfirmedDumpLocationIds,
    setEmptyAvailableCans,
    setLocationChoosingMode,
    setWizardLocationId,
    setLocationDropLot,
    setLocationCustomAddress,
    setChosenDumpsterIds,
    setRouteResult,
    resetWorkflow,
    setReturnToSummaryAvailable,
} = workflowSlice.actions;

export default workflowSlice.reducer;

// Selectors
export const selectStoredRouteType = state => state.workflow.storedRouteType;
export const selectConfirmedDumpLocationIds = state => state.workflow.confirmedDumpLocationIds;
export const selectEmptyAvailableCans = state => state.workflow.emptyAvailableCans;
export const selectStartLocationChoosingMode = state => state.workflow.startLocationChoosingMode;
export const selectStartLocation = state => {
    const mode = selectStartLocationChoosingMode(state);
    switch (mode) {
        case constants.LOCATION_CHOOSING_MODES.current:
            return state.workflow.wizardLocationId;
        case constants.LOCATION_CHOOSING_MODES.dropLot:
            return state.workflow.startLocationDropLot;
        case constants.LOCATION_CHOOSING_MODES.custom:
            return state.workflow.startLocationCustomAddress;
        default:
            throw new Error(`Unknown case in selectStartLocation of workflowSlice.js: ${mode}`);
    }
};
export const selectEndLocationChoosingMode = state => state.workflow.endLocationChoosingMode;
export const selectEndLocation = state => {
    const mode = selectEndLocationChoosingMode(state);
    switch (mode) {
        case constants.LOCATION_CHOOSING_MODES.current:
            return state.workflow.wizardLocationId;
        case constants.LOCATION_CHOOSING_MODES.dropLot:
            return state.workflow.endLocationDropLot;
        case constants.LOCATION_CHOOSING_MODES.custom:
            return state.workflow.endLocationCustomAddress;
        default:
            throw new Error(`Unknown case in selectEndLocation of workflowSlice.js: ${mode}`);
    }
};
export const selectChosenDumpsterIds = state => state.workflow.chosenDumpsterIds;
export const selectReturnToSummaryAvailable = state => state.workflow.returnToSummaryAvailable;
export const selectWizardLocationId = state => state.workflow.wizardLocationId;